// ── BeeLove Category Tree ────────────────────────────────────────────────
// Single source of truth for all profile attributes, interests, values and
// lifestyle signals. Used for:
//   1. Hive group definitions
//   2. Event audience targeting / sponsored notifications
//   3. Admin ad placement targeting
//   4. Profile completion & discovery filters
//
// Tree structure:
//   domain > category > subcategory > leaf (concrete value / tag)
//
// Each node has:
//   id       — dot-notation path  (e.g. "lifestyle.diet.vegan")
//   label    — display name
//   type     — 'domain' | 'category' | 'subcategory' | 'value' | 'interest'
//   field    — which user profile field this maps to (for targeting)
//   match    — function(user) → bool  (for event targeting)
//   children — nested nodes

import { getEffectiveTranslations } from './i18n';
function t(lang, key) { return getEffectiveTranslations(lang||'en')[key] || key; }

// ─────────────────────────────────────────────────────────────────────────────
// RAW TREE DEFINITION
// Each leaf: { id, labelEN, labelFR, field, value, icon? }
// ─────────────────────────────────────────────────────────────────────────────

export const CATEGORY_TREE_DEF = {
  id: 'root',
  labelEN: 'All Attributes', labelFR: 'Tous les attributs',
  type: 'domain',
  children: [

    // ── DEMOGRAPHICS ────────────────────────────────────────────────────────
    {
      id: 'demographics', labelEN: 'Demographics', labelFR: 'Démographie',
      type: 'category', icon: '👤',
      children: [
        {
          id: 'demographics.age', labelEN: 'Age', labelFR: 'Âge',
          type: 'subcategory', field: 'age',
          children: [
            { id:'demographics.age.18_24', labelEN:'18–24', labelFR:'18–24', field:'age', match: u => u.age>=18&&u.age<=24 },
            { id:'demographics.age.25_29', labelEN:'25–29', labelFR:'25–29', field:'age', match: u => u.age>=25&&u.age<=29 },
            { id:'demographics.age.30_35', labelEN:'30–35', labelFR:'30–35', field:'age', match: u => u.age>=30&&u.age<=35 },
            { id:'demographics.age.36_45', labelEN:'36–45', labelFR:'36–45', field:'age', match: u => u.age>=36&&u.age<=45 },
            { id:'demographics.age.45plus', labelEN:'45+', labelFR:'45+', field:'age', match: u => u.age>=45 },
          ],
        },
        {
          id: 'demographics.gender', labelEN: 'Gender', labelFR: 'Genre',
          type: 'subcategory', field: 'gender',
          children: [
            { id:'demographics.gender.man',       labelEN:'Man',            labelFR:'Homme',           field:'gender', match: u=>u.gender==='man' },
            { id:'demographics.gender.woman',     labelEN:'Woman',          labelFR:'Femme',           field:'gender', match: u=>u.gender==='woman' },
            { id:'demographics.gender.nonbinary', labelEN:'Non-binary',     labelFR:'Non-binaire',     field:'gender', match: u=>u.gender==='nonbinary' },
          ],
        },
        {
          id: 'demographics.city', labelEN: 'City / Network', labelFR: 'Ville / Réseau',
          type: 'subcategory', field: 'city',
          children: [
            { id:'demographics.city.montreal', labelEN:'Montréal', labelFR:'Montréal', field:'network_id', match: u=>u.network_id==='ca_montreal' },
            { id:'demographics.city.lorient',  labelEN:'Lorient',  labelFR:'Lorient',  field:'network_id', match: u=>u.network_id==='fr_lorient' },
            { id:'demographics.city.paris',    labelEN:'Paris',    labelFR:'Paris',    field:'network_id', match: u=>u.network_id==='fr_paris' },
            { id:'demographics.city.lyon',     labelEN:'Lyon',     labelFR:'Lyon',     field:'network_id', match: u=>u.network_id==='lyon' },
          ],
        },
        {
          id: 'demographics.language', labelEN: 'Language', labelFR: 'Langue',
          type: 'subcategory', field: 'languages',
          children: [
            { id:'demographics.language.french',  labelEN:'French speaker',  labelFR:'Francophone',  field:'languages', match: u=>(u.languages||[]).includes('French') },
            { id:'demographics.language.english', labelEN:'English speaker', labelFR:'Anglophone',   field:'languages', match: u=>(u.languages||[]).includes('English') },
            { id:'demographics.language.bilingual',labelEN:'Bilingual',      labelFR:'Bilingue',     field:'languages', match: u=>(u.languages||[]).length>=2 },
          ],
        },
      ],
    },

    // ── LIFESTYLE ───────────────────────────────────────────────────────────
    {
      id: 'lifestyle', labelEN: 'Lifestyle', labelFR: 'Mode de vie',
      type: 'category', icon: '🌿',
      children: [
        {
          id: 'lifestyle.diet', labelEN: 'Diet', labelFR: 'Alimentation',
          type: 'subcategory', field: 'diet',
          children: [
            { id:'lifestyle.diet.vegan',       labelEN:'Vegan',        labelFR:'Végane',         field:'diet', match:u=>u.diet==='vegan' },
            { id:'lifestyle.diet.vegetarian',  labelEN:'Vegetarian',   labelFR:'Végétarien(ne)', field:'diet', match:u=>u.diet==='vegetarian' },
            { id:'lifestyle.diet.plantbased',  labelEN:'Plant-based',  labelFR:'Végétal',        field:'diet', match:u=>u.diet==='vegan'||u.diet==='vegetarian' },
            { id:'lifestyle.diet.omnivore',    labelEN:'Omnivore',     labelFR:'Omnivore',       field:'diet', match:u=>u.diet==='omnivore'||!u.diet },
            { id:'lifestyle.diet.halal',       labelEN:'Halal',        labelFR:'Halal',          field:'diet', match:u=>u.diet==='halal' },
            { id:'lifestyle.diet.kosher',      labelEN:'Kosher',       labelFR:'Casher',         field:'diet', match:u=>u.diet==='kosher' },
          ],
        },
        {
          id: 'lifestyle.exercise', labelEN: 'Exercise', labelFR: 'Activité physique',
          type: 'subcategory', field: 'exercise',
          children: [
            { id:'lifestyle.exercise.daily',   labelEN:'Daily',          labelFR:'Quotidien',     field:'exercise', match:u=>u.exercise==='daily' },
            { id:'lifestyle.exercise.3_4',     labelEN:'3–4×/week',      labelFR:'3–4×/sem.',     field:'exercise', match:u=>u.exercise==='3-4x/week' },
            { id:'lifestyle.exercise.1_2',     labelEN:'1–2×/week',      labelFR:'1–2×/sem.',     field:'exercise', match:u=>u.exercise==='1-2x/week' },
            { id:'lifestyle.exercise.rarely',  labelEN:'Rarely',         labelFR:'Rarement',      field:'exercise', match:u=>u.exercise==='rarely' },
            { id:'lifestyle.exercise.never',   labelEN:'Never',          labelFR:'Jamais',        field:'exercise', match:u=>u.exercise==='never' },
          ],
        },
        {
          id: 'lifestyle.drinking', labelEN: 'Drinking', labelFR: 'Alcool',
          type: 'subcategory', field: 'drinking',
          children: [
            { id:'lifestyle.drinking.never',     labelEN:'Never',      labelFR:'Jamais',         field:'drinking', match:u=>u.drinking==='never' },
            { id:'lifestyle.drinking.socially',  labelEN:'Socially',   labelFR:'Socialement',    field:'drinking', match:u=>u.drinking==='socially' },
            { id:'lifestyle.drinking.regularly', labelEN:'Regularly',  labelFR:'Régulièrement',  field:'drinking', match:u=>u.drinking==='regularly' },
          ],
        },
        {
          id: 'lifestyle.smoking', labelEN: 'Smoking / Cannabis', labelFR: 'Tabac / Cannabis',
          type: 'subcategory', field: 'smoking',
          children: [
            { id:'lifestyle.smoking.no',      labelEN:'Non-smoker',  labelFR:'Non-fumeur/se', field:'smoking', match:u=>u.smoking==='never' },
            { id:'lifestyle.smoking.yes',     labelEN:'Smoker',      labelFR:'Fumeur/se',     field:'smoking', match:u=>u.smoking==='daily'||u.smoking==='socially' },
            { id:'lifestyle.cannabis.no',     labelEN:'No cannabis', labelFR:'Sans cannabis', field:'cannabis', match:u=>u.cannabis==='never' },
            { id:'lifestyle.cannabis.yes',    labelEN:'Cannabis',    labelFR:'Cannabis',      field:'cannabis', match:u=>u.cannabis==='socially'||u.cannabis==='regularly' },
          ],
        },
        {
          id: 'lifestyle.pets', labelEN: 'Pets', labelFR: 'Animaux',
          type: 'subcategory', field: 'pets',
          children: [
            { id:'lifestyle.pets.cat',    labelEN:'Cat owner',  labelFR:'Propriétaire de chat', field:'pets', match:u=>u.pets==='cat' },
            { id:'lifestyle.pets.dog',    labelEN:'Dog owner',  labelFR:'Propriétaire de chien', field:'pets', match:u=>u.pets==='dog' },
            { id:'lifestyle.pets.any',    labelEN:'Has pets',   labelFR:'A des animaux', field:'pets', match:u=>u.pets&&u.pets!=='none'&&u.pets!=='allergic' },
            { id:'lifestyle.pets.none',   labelEN:'No pets',    labelFR:'Sans animaux', field:'pets', match:u=>u.pets==='none' },
          ],
        },
        {
          id: 'lifestyle.social', labelEN: 'Social energy', labelFR: 'Énergie sociale',
          type: 'subcategory',
          children: [
            { id:'lifestyle.social.introvert', labelEN:'Introvert',  labelFR:'Introverti(e)',  field:'quiz_answers', match:u=>u.quiz_answers?.social===1||u.quiz_answers?.social===2 },
            { id:'lifestyle.social.extrovert', labelEN:'Extrovert',  labelFR:'Extraverti(e)',  field:'quiz_answers', match:u=>u.quiz_answers?.social===3||u.quiz_answers?.social===4 },
          ],
        },
      ],
    },

    // ── VALUES ──────────────────────────────────────────────────────────────
    {
      id: 'values', labelEN: 'Values', labelFR: 'Valeurs',
      type: 'category', icon: '⚖️',
      children: [
        {
          id: 'values.religion', labelEN: 'Religion / Spirituality', labelFR: 'Religion / Spiritualité',
          type: 'subcategory', field: 'religion',
          children: [
            { id:'values.religion.none',       labelEN:'Agnostic / Atheist',  labelFR:'Agnostique / Athée',    field:'religion', match:u=>u.religion==='agnostic'||u.religion==='atheist' },
            { id:'values.religion.christian',  labelEN:'Christian',           labelFR:'Chrétien(ne)',          field:'religion', match:u=>u.religion==='christian' },
            { id:'values.religion.muslim',     labelEN:'Muslim',              labelFR:'Musulman(e)',           field:'religion', match:u=>u.religion==='muslim' },
            { id:'values.religion.jewish',     labelEN:'Jewish',              labelFR:'Juif/Juive',            field:'religion', match:u=>u.religion==='jewish' },
            { id:'values.religion.spiritual',  labelEN:'Spiritual',           labelFR:'Spirituel(le)',         field:'religion', match:u=>u.religion==='spiritual' },
            { id:'values.religion.other',      labelEN:'Other faith',         labelFR:'Autre religion',        field:'religion', match:u=>!!u.religion&&!['agnostic','atheist','christian','muslim','jewish','spiritual'].includes(u.religion) },
          ],
        },
        {
          id: 'values.politics', labelEN: 'Politics', labelFR: 'Politique',
          type: 'subcategory', field: 'politics',
          children: [
            { id:'values.politics.left',         labelEN:'Left-leaning',    labelFR:'Gauche',       field:'politics', match:u=>u.politics==='left' },
            { id:'values.politics.centerleft',   labelEN:'Centre-left',     labelFR:'Centre-gauche',field:'politics', match:u=>u.politics==='center-left' },
            { id:'values.politics.moderate',     labelEN:'Moderate / Apolitical', labelFR:'Modéré(e) / Apolitique', field:'politics', match:u=>u.politics==='moderate'||!u.politics },
            { id:'values.politics.centerright',  labelEN:'Centre-right',    labelFR:'Centre-droit', field:'politics', match:u=>u.politics==='center-right' },
          ],
        },
        {
          id: 'values.relationship', labelEN: 'Relationship goal', labelFR: 'Objectif relationnel',
          type: 'subcategory', field: 'relationship_goal',
          children: [
            { id:'values.relationship.serious',    labelEN:'Serious relationship', labelFR:'Relation sérieuse', field:'relationship_goal', match:u=>u.relationship_goal==='relationship' },
            { id:'values.relationship.casual',     labelEN:'Casual',               labelFR:'Sans engagement',   field:'relationship_goal', match:u=>u.relationship_goal==='casual' },
            { id:'values.relationship.friends',    labelEN:'Friends first',         labelFR:'Amis d\'abord',    field:'relationship_goal', match:u=>u.relationship_goal==='friendship' },
            { id:'values.relationship.marriage',   labelEN:'Marriage',              labelFR:'Mariage',          field:'relationship_goal', match:u=>u.relationship_goal==='marriage' },
            { id:'values.relationship.open',       labelEN:'Open to anything',      labelFR:'Ouvert(e)',        field:'relationship_goal', match:u=>!u.relationship_goal||u.relationship_goal==='unsure' },
          ],
        },
        {
          id: 'values.love_language', labelEN: 'Love language', labelFR: 'Langage d\'amour',
          type: 'subcategory', field: 'love_language',
          children: [
            { id:'values.love.words',   labelEN:'Words of affirmation', labelFR:'Paroles valorisantes', field:'love_language', match:u=>u.love_language==='words' },
            { id:'values.love.acts',    labelEN:'Acts of service',      labelFR:'Actes de service',     field:'love_language', match:u=>u.love_language==='acts' },
            { id:'values.love.gifts',   labelEN:'Gifts',                labelFR:'Cadeaux',              field:'love_language', match:u=>u.love_language==='gifts' },
            { id:'values.love.quality', labelEN:'Quality time',         labelFR:'Temps de qualité',     field:'love_language', match:u=>u.love_language==='quality' },
            { id:'values.love.touch',   labelEN:'Physical touch',       labelFR:'Contact physique',     field:'love_language', match:u=>u.love_language==='touch' },
          ],
        },
        {
          id: 'values.children', labelEN: 'Children', labelFR: 'Enfants',
          type: 'subcategory',
          children: [
            { id:'values.children.wants',    labelEN:'Wants children',   labelFR:'Veut des enfants', field:'quiz_answers', match:u=>u.quiz_answers?.stakes===4 },
            { id:'values.children.no',       labelEN:'No children',      labelFR:'Sans enfants',     field:'quiz_answers', match:u=>u.quiz_answers?.stakes===1 },
            { id:'values.children.open',     labelEN:'Open / Undecided', labelFR:'Ouvert(e)',         field:'quiz_answers', match:u=>u.quiz_answers?.stakes===2||u.quiz_answers?.stakes===3 },
          ],
        },
      ],
    },

    // ── EDUCATION & CAREER ───────────────────────────────────────────────────
    {
      id: 'education', labelEN: 'Education & Career', labelFR: 'Études & Carrière',
      type: 'category', icon: '🎓',
      children: [
        {
          id: 'education.level', labelEN: 'Education level', labelFR: 'Niveau d\'études',
          type: 'subcategory', field: 'education',
          children: [
            { id:'education.level.highschool', labelEN:'High school',  labelFR:'Lycée / Secondaire', field:'education', match:u=>u.education==='highschool' },
            { id:'education.level.college',    labelEN:'Some college', labelFR:'Collège',           field:'education', match:u=>u.education==='some-college' },
            { id:'education.level.bachelors',  labelEN:'Bachelor\'s',  labelFR:'Baccalauréat',     field:'education', match:u=>u.education==='bachelors' },
            { id:'education.level.masters',    labelEN:'Master\'s',    labelFR:'Maîtrise',          field:'education', match:u=>u.education==='masters' },
            { id:'education.level.phd',        labelEN:'PhD',          labelFR:'Doctorat',          field:'education', match:u=>u.education==='phd' },
            { id:'education.level.trade',      labelEN:'Trade / Vocational', labelFR:'Formation professionnelle', field:'education', match:u=>u.education==='trade' },
          ],
        },
        {
          id: 'education.field', labelEN: 'Field of study', labelFR: 'Domaine',
          type: 'subcategory', field: 'educationField',
          children: [
            { id:'education.field.tech',     labelEN:'Tech / Engineering', labelFR:'Tech / Ingénierie', field:'education_field', match:u=>/tech|computer|software|engineer|it\b/i.test(u.education_field||'') },
            { id:'education.field.arts',     labelEN:'Arts / Design',      labelFR:'Arts / Design',    field:'education_field', match:u=>/art|design|music|film/i.test(u.education_field||'') },
            { id:'education.field.business', labelEN:'Business',           labelFR:'Commerce',         field:'education_field', match:u=>/business|management|finance|marketing/i.test(u.education_field||'') },
            { id:'education.field.science',  labelEN:'Science',            labelFR:'Sciences',         field:'education_field', match:u=>/science|biology|chemistry|physics|math/i.test(u.education_field||'') },
            { id:'education.field.social',   labelEN:'Social sciences / Humanities', labelFR:'Sciences humaines', field:'education_field', match:u=>/social|human|psycho|socio|history|law/i.test(u.education_field||'') },
          ],
        },
      ],
    },

    // ── INTERESTS ────────────────────────────────────────────────────────────
    {
      id: 'interests', labelEN: 'Interests', labelFR: 'Intérêts',
      type: 'category', icon: '✨',
      children: [
        {
          id: 'interests.arts_culture', labelEN: 'Arts & Culture', labelFR: 'Arts & Culture',
          type: 'subcategory',
          children: [
            { id:'interests.arts_culture.music',       labelEN:'Music',           labelFR:'Musique',          field:'interests', match:u=>(u.interests||[]).some(i=>['interest.music','interest.jazz','interest.classical','interest.vinyl','interest.concerts'].includes(i)) },
            { id:'interests.arts_culture.visual_arts', labelEN:'Visual arts',     labelFR:'Arts visuels',     field:'interests', match:u=>(u.interests||[]).some(i=>['interest.art','interest.design','interest.photography2','interest.illustration'].includes(i)) },
            { id:'interests.arts_culture.cinema',      labelEN:'Cinema / Film',   labelFR:'Cinéma',           field:'interests', match:u=>(u.interests||[]).some(i=>['interest.cinema','interest.film','interest.netflix'].includes(i)) },
            { id:'interests.arts_culture.theatre',     labelEN:'Theatre / Dance', labelFR:'Théâtre / Danse',  field:'interests', match:u=>(u.interests||[]).some(i=>['interest.theatre','interest.dance','interest.ballet'].includes(i)) },
            { id:'interests.arts_culture.literature',  labelEN:'Literature',      labelFR:'Littérature',      field:'interests', match:u=>(u.interests||[]).some(i=>['interest.reading','interest.books','interest.poetry'].includes(i)) },
          ],
        },
        {
          id: 'interests.sports', labelEN: 'Sports & Outdoors', labelFR: 'Sport & Nature',
          type: 'subcategory',
          children: [
            { id:'interests.sports.outdoor',   labelEN:'Outdoor / Hiking', labelFR:'Plein air / Randonnée', field:'interests', match:u=>(u.interests||[]).some(i=>['interest.hiking','interest.camping','interest.alpinism','interest.outdoor'].includes(i)) },
            { id:'interests.sports.water',     labelEN:'Water sports',     labelFR:'Sports nautiques',      field:'interests', match:u=>(u.interests||[]).some(i=>['interest.surfing','interest.swimming','interest.kayak','interest.sailing','interest.ocean'].includes(i)) },
            { id:'interests.sports.team',      labelEN:'Team sports',      labelFR:'Sports d\'équipe',      field:'interests', match:u=>(u.interests||[]).some(i=>['interest.soccer','interest.basketball','interest.volleyball'].includes(i)) },
            { id:'interests.sports.fitness',   labelEN:'Fitness / Yoga',   labelFR:'Fitness / Yoga',        field:'interests', match:u=>(u.interests||[]).some(i=>['interest.fitness','interest.yoga','interest.climbing','interest.cycling'].includes(i)) },
          ],
        },
        {
          id: 'interests.food_drink', labelEN: 'Food & Drink', labelFR: 'Gastronomie',
          type: 'subcategory',
          children: [
            { id:'interests.food.coffee',    labelEN:'Coffee',         labelFR:'Café',               field:'interests', match:u=>(u.interests||[]).includes('interest.coffee') },
            { id:'interests.food.wine',      labelEN:'Wine / Cocktails', labelFR:'Vin / Cocktails',  field:'interests', match:u=>(u.interests||[]).some(i=>['interest.wine','interest.cocktails','interest.champagne'].includes(i)) },
            { id:'interests.food.cooking',   labelEN:'Cooking / Baking', labelFR:'Cuisine / Pâtisserie', field:'interests', match:u=>(u.interests||[]).some(i=>['interest.cooking','interest.baking','interest.bbq'].includes(i)) },
            { id:'interests.food.foodie',    labelEN:'Foodie / Restaurants', labelFR:'Gastronomie',  field:'interests', match:u=>(u.interests||[]).some(i=>['interest.food','interest.restaurants','interest.sushi'].includes(i)) },
          ],
        },
        {
          id: 'interests.tech_science', labelEN: 'Tech & Science', labelFR: 'Tech & Sciences',
          type: 'subcategory',
          children: [
            { id:'interests.tech.coding',  labelEN:'Coding / Dev',   labelFR:'Code / Dev',          field:'interests', match:u=>(u.interests||[]).some(i=>['interest.coding','interest.tech','interest.ai','interest.startups'].includes(i)) },
            { id:'interests.tech.ai',      labelEN:'AI / ML',        labelFR:'IA / ML',              field:'interests', match:u=>(u.interests||[]).includes('interest.ai') },
            { id:'interests.tech.science', labelEN:'Science',        labelFR:'Sciences',             field:'interests', match:u=>(u.interests||[]).some(i=>['interest.science','interest.astronomy','interest.space'].includes(i)) },
            { id:'interests.tech.gaming',  labelEN:'Gaming',         labelFR:'Jeux vidéo',           field:'interests', match:u=>(u.interests||[]).some(i=>['interest.gaming','interest.vr','interest.esports'].includes(i)) },
          ],
        },
        {
          id: 'interests.social_impact', labelEN: 'Social & Impact', labelFR: 'Social & Impact',
          type: 'subcategory',
          children: [
            { id:'interests.social.activism',     labelEN:'Activism',         labelFR:'Activisme',          field:'interests', match:u=>(u.interests||[]).includes('interest.activism') },
            { id:'interests.social.environment',  labelEN:'Environment',      labelFR:'Environnement',      field:'interests', match:u=>(u.interests||[]).some(i=>['interest.environment','interest.sustainability','interest.ecology'].includes(i)) },
            { id:'interests.social.community',    labelEN:'Community',        labelFR:'Communauté',         field:'interests', match:u=>(u.interests||[]).some(i=>['interest.community','interest.volunteering','interest.nonprofit'].includes(i)) },
            { id:'interests.social.languages',    labelEN:'Languages / Travel',labelFR:'Langues / Voyage',  field:'interests', match:u=>(u.interests||[]).some(i=>['interest.travel','interest.languages','interest.languages2'].includes(i)) },
          ],
        },
        {
          id: 'interests.mindfulness', labelEN: 'Mindfulness & Wellness', labelFR: 'Bien-être & Mindfulness',
          type: 'subcategory',
          children: [
            { id:'interests.mind.meditation', labelEN:'Meditation',     labelFR:'Méditation',          field:'interests', match:u=>(u.interests||[]).some(i=>['interest.meditation','interest.mindfulness','interest.spirituality'].includes(i)) },
            { id:'interests.mind.wellness',   labelEN:'Wellness',       labelFR:'Bien-être',           field:'interests', match:u=>(u.interests||[]).some(i=>['interest.wellness','interest.health','interest.naturotherapy'].includes(i)) },
            { id:'interests.mind.astrology',  labelEN:'Astrology',      labelFR:'Astrologie',          field:'interests', match:u=>(u.interests||[]).some(i=>['interest.astrology','interest.tarot'].includes(i)) },
          ],
        },
      ],
    },

    // ── PERSONALITY / COMPATIBILITY ────────────────────────────────────────
    {
      id: 'personality', labelEN: 'Personality & Compatibility', labelFR: 'Personnalité & Compatibilité',
      type: 'category', icon: '🧠',
      children: [
        {
          id: 'personality.mbti', labelEN: 'MBTI', labelFR: 'MBTI',
          type: 'subcategory', field: 'mbti',
          children: [
            { id:'personality.mbti.introvert',  labelEN:'Introvert (I___)', labelFR:'Introverti(e) (I___)', field:'mbti', match:u=>(u.mbti||'')[0]==='I' },
            { id:'personality.mbti.extrovert',  labelEN:'Extrovert (E___)', labelFR:'Extraverti(e) (E___)', field:'mbti', match:u=>(u.mbti||'')[0]==='E' },
            { id:'personality.mbti.intuitive',  labelEN:'Intuitive (__N_)', labelFR:'Intuitif/ve (__N_)',   field:'mbti', match:u=>(u.mbti||'')[1]==='N' },
            { id:'personality.mbti.sensing',    labelEN:'Sensing (__S_)',   labelFR:'Sensoriel(le) (__S_)', field:'mbti', match:u=>(u.mbti||'')[1]==='S' },
          ],
        },
        {
          id: 'personality.zodiac', labelEN: 'Zodiac', labelFR: 'Zodiaque',
          type: 'subcategory', field: 'zodiac',
          children: [
            { id:'personality.zodiac.fire',  labelEN:'Fire signs (Aries, Leo, Sag)', labelFR:'Signes de feu', field:'zodiac', match:u=>['Aries','Leo','Sagittarius'].includes(u.zodiac) },
            { id:'personality.zodiac.earth', labelEN:'Earth signs (Taurus, Virgo, Cap)', labelFR:'Signes de terre', field:'zodiac', match:u=>['Taurus','Virgo','Capricorn'].includes(u.zodiac) },
            { id:'personality.zodiac.air',   labelEN:'Air signs (Gemini, Libra, Aqua)', labelFR:'Signes d\'air', field:'zodiac', match:u=>['Gemini','Libra','Aquarius'].includes(u.zodiac) },
            { id:'personality.zodiac.water', labelEN:'Water signs (Cancer, Scorpio, Pisces)', labelFR:'Signes d\'eau', field:'zodiac', match:u=>['Cancer','Scorpio','Pisces'].includes(u.zodiac) },
          ],
        },
        {
          id: 'personality.quiz', labelEN: 'Compatibility quiz', labelFR: 'Quiz de compatibilité',
          type: 'subcategory',
          children: [
            { id:'personality.quiz.completed', labelEN:'Quiz completed', labelFR:'Quiz complété', field:'quiz_answers', match:u=>u.quiz_answers&&Object.keys(u.quiz_answers).length>=8 },
          ],
        },
      ],
    },

    // ── APP STATUS ───────────────────────────────────────────────────────────
    {
      id: 'app_status', labelEN: 'App Status', labelFR: 'Statut app',
      type: 'category', icon: '🐝',
      children: [
        {
          id: 'app_status.subscription', labelEN: 'Subscription', labelFR: 'Abonnement',
          type: 'subcategory', field: 'plan',
          children: [
            { id:'app_status.sub.free',  labelEN:'Free plan',   labelFR:'Plan gratuit',  field:'plan', match:u=>!u.plan||u.plan==='free' },
            { id:'app_status.sub.honey', labelEN:'Honey plan',  labelFR:'Plan Honey',    field:'plan', match:u=>u.plan==='honey' },
            { id:'app_status.sub.royal', labelEN:'Royal plan',  labelFR:'Plan Royal',    field:'plan', match:u=>u.plan==='royal' },
            { id:'app_status.sub.paid',  labelEN:'Any paid',    labelFR:'Payant',        field:'plan', match:u=>['honey','royal'].includes(u.plan) },
          ],
        },
        {
          id: 'app_status.engagement', labelEN: 'Engagement', labelFR: 'Engagement',
          type: 'subcategory',
          children: [
            { id:'app_status.eng.active',     labelEN:'Active (last 7 days)',     labelFR:'Actif/ve (7 derniers jours)', field:'last_active',   match:u=>u.last_active&&(Date.now()-new Date(u.last_active).getTime())<7*86400000 },
            { id:'app_status.eng.verified',   labelEN:'Verified profile',         labelFR:'Profil vérifié',              field:'verified',      match:u=>!!u.verified },
            { id:'app_status.eng.invited',    labelEN:'Has invited friends',       labelFR:'A invité des amis',           field:'invite_count',  match:u=>(u.invite_count||0)>0 },
            { id:'app_status.eng.new',        labelEN:'New member (< 30 days)',    labelFR:'Nouveau membre',              field:'created_at',    match:u=>!u.created_at||(Date.now()-new Date(u.created_at).getTime())<30*86400000 },
          ],
        },
        {
          id: 'app_status.hive_role', labelEN: 'Hive role', labelFR: 'Rôle dans la ruche',
          type: 'subcategory', field: 'role',
          children: [
            { id:'app_status.role.scout',     labelEN:'Scout',     labelFR:'Éclaireur/se', field:'role', match:u=>u.role==='scout' },
            { id:'app_status.role.builder',   labelEN:'Builder',   labelFR:'Bâtisseur/se', field:'role', match:u=>u.role==='builder' },
            { id:'app_status.role.connector', labelEN:'Connector', labelFR:'Connecteur/se', field:'role', match:u=>u.role==='connector' },
            { id:'app_status.role.queen',     labelEN:'Queen Bee', labelFR:'Reine des abeilles', field:'role', match:u=>u.role==='queen' },
          ],
        },
      ],
    },

  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

// Flatten tree to get all leaves
export function getAllLeaves(node = CATEGORY_TREE_DEF) {
  if (!node.children || node.children.length === 0) return [node];
  return node.children.flatMap(child => getAllLeaves(child));
}

// Get all nodes at a given depth (0=root, 1=domain, 2=category, 3=subcategory, 4=leaf)
export function getNodesByDepth(depth, node = CATEGORY_TREE_DEF, currentDepth = 0) {
  if (currentDepth === depth) return [node];
  if (!node.children) return [];
  return node.children.flatMap(c => getNodesByDepth(depth, c, currentDepth + 1));
}

// Get a node by its dot-notation id
export function getNodeById(id, node = CATEGORY_TREE_DEF) {
  if (node.id === id) return node;
  if (!node.children) return null;
  for (const child of node.children) {
    const found = getNodeById(id, child);
    if (found) return found;
  }
  return null;
}

// Get ancestors of a node
export function getAncestors(id, node = CATEGORY_TREE_DEF, path = []) {
  if (node.id === id) return path;
  if (!node.children) return null;
  for (const ch of node.children) {
    const found = getAncestors(id, ch, [...path, node]);
    if (found) return found;
  }
  return null;
}

// Get label in given language
export function getLabel(node, lang = 'en') {
  return lang === 'fr' ? node.labelFR : node.labelEN;
}

// Apply a set of targeting criteria to a user.
// Logic: OR within the same parent group, AND across different parent groups.
// e.g. [age.25_29, age.30_35, gender.woman] → (25-29 OR 30-35) AND (woman)
// Special case: if ALL matchable leaves of a parent are selected, skip that group
// (selecting every option = no filter on that dimension = matches everyone).
export function matchesTargeting(user, selectedIds) {
  if (!selectedIds || selectedIds.length === 0) return true;

  // Group by actual tree parent (not id prefix) so nodes whose ids don't match
  // their structural parent (e.g. lifestyle.cannabis.* inside lifestyle.smoking)
  // still land in the same OR-group.
  const groups = {};
  for (const id of selectedIds) {
    const node = getNodeById(id);
    if (!node) continue;
    const matchNodes = node.match
      ? [node]
      : getAllLeaves(node).filter(l => l.match);
    if (matchNodes.length === 0) continue;
    const ancestors = getAncestors(id);
    const parentNode = ancestors && ancestors.length > 0 ? ancestors[ancestors.length - 1] : null;
    const groupKey = parentNode ? parentNode.id : 'root';
    if (!groups[groupKey]) groups[groupKey] = { nodes: [], parentNode };
    groups[groupKey].nodes.push(...matchNodes);
  }

  return Object.values(groups).every(({ nodes, parentNode }) => {
    // If every matchable leaf under this parent is selected → no filter on this dimension
    if (parentNode) {
      const totalLeaves = getAllLeaves(parentNode).filter(l => l.match).length;
      if (nodes.length >= totalLeaves) return true;
    }
    return nodes.some(n => n.match(user));
  });
}

// Count how many users in an array match a targeting selection
export function countMatches(users, selectedLeafIds) {
  return users.filter(u => matchesTargeting(u, selectedLeafIds)).length;
}

// Get all top-level categories (depth 2)
export function getTopCategories() {
  return CATEGORY_TREE_DEF.children;
}

// Convert tree to flat list for dropdowns
export function flattenForSelect(lang = 'en', node = CATEGORY_TREE_DEF, depth = 0) {
  const result = [];
  if (depth > 0) {
    result.push({ id: node.id, label: getLabel(node, lang), depth, hasChildren: !!(node.children?.length), leaf: !node.children });
  }
  if (node.children) {
    node.children.forEach(child => result.push(...flattenForSelect(lang, child, depth + 1)));
  }
  return result;
}

// Returns CATEGORY_TREE_DEF augmented with a "Target Audiences" domain built
// from the saved audiences in localStorage (set by the Admin categories tab).
// Safe to call on every render — localStorage reads are synchronous and cheap.
// Group leaf IDs by their immediate parent (dot-notation prefix).
// Returns an array of groups, each being an array of IDs sharing the same parent.
function groupByParent(ids) {
  const map = {};
  for (const id of ids) {
    const parts = id.split('.');
    const parent = parts.length > 1 ? parts.slice(0, -1).join('.') : id;
    (map[parent] = map[parent] || []).push(id);
  }
  return Object.values(map);
}

export function buildCategoryTree() {
  let audiences = [];
  try { audiences = JSON.parse(localStorage.getItem('beelove_audiences') || '[]'); } catch {}
  if (!audiences.length) return CATEGORY_TREE_DEF;
  return {
    ...CATEGORY_TREE_DEF,
    children: [
      ...CATEGORY_TREE_DEF.children,
      {
        id: 'target_audiences',
        labelEN: 'Target Audiences',
        labelFR: 'Audiences cibles',
        type: 'domain',
        icon: '🎯',
        children: audiences.map(a => {
          // Resolve human-readable labels; never expose raw ta.* or dotted IDs
          const criteriaLabels = (a.criteriaLabels || []).length
            ? a.criteriaLabels.filter(l => !l.startsWith('ta.'))
            : a.categoryIds.map(id => {
                const node = getNodeById(id);
                return node ? getLabel(node, 'fr') : id.split('.').pop();
              });

          // OR within same subcategory, AND across subcategories
          const groups = groupByParent(a.categoryIds);
          const match = u => {
            const interests = u.interests || [];
            return groups.every(group => interests.some(i => group.includes(i)));
          };

          return {
            id: `ta.${a.name}`,
            labelEN: a.name,
            labelFR: a.name,
            type: 'leaf',
            criteriaLabels,
            match,
          };
        }),
      },
    ],
  };
}
